import { Resource } from 'modules/core/base-resource';
import { Roles } from 'modules/roles-manager/domain/interfaces/role';
import { OrganizerUserDto, OrganizerUser } from 'modules/roles-manager/domain/interfaces/organizer-user';
import { mapOrganizerUserResponseDto } from 'modules/roles-manager/domain/data-mappers/organizer-user.data-mapper';
import playersResource from 'modules/players/domain/resources/players-resource';
import httpResource from 'modules/core/http-resource';

export type UpdateUserPhotoPayload = { photo: File };
export type GetUsersPayload = {
  limit?: number;
  offset?: number;
  name?: string;
  surname?: string;
  middleName?: string;
  phone?: string;
  isActive?: number;
};

export type CreateUserPayload = Pick<OrganizerUser, 'name' | 'surname' | 'birthDate' | 'middleName' | 'email' | 'phone'> & {
  role: Roles;
};
export type UpdateUserPayload = Pick<OrganizerUser, 'id' | 'name' | 'surname' | 'birthDate' | 'middleName' | 'isActive'> & {
  role: Roles;
} & Partial<UpdateUserPhotoPayload>;
export type DeleteUserPayload = Pick<OrganizerUser, 'id'>;

class RoleUsers extends Resource {
  constructor() {
    super('tournaments', httpResource);
  }

  public async getUsers(tournamentId: number, params: GetUsersPayload): Promise<OrganizerUser[]> {
    const result = await this.child(`${tournamentId}/users`).get<OrganizerUserDto[]>(params);
    return mapOrganizerUserResponseDto(result);
  }

  public async createUser(tournamentId: number, user: CreateUserPayload): Promise<OrganizerUser> {
    const result = await this.child(`${tournamentId}/users`).post<OrganizerUserDto>(user);
    return mapOrganizerUserResponseDto([result])[0];
  }

  public async updateUser(tournamentId: number, user: UpdateUserPayload): Promise<OrganizerUser> {
    const { id, birthDate, ...rest } = user;
    const result = await this.child(`${tournamentId}/users/${id}`).put<OrganizerUserDto>({
      ...rest,
      birthDate: birthDate ?? new Date(0).toISOString(),
    } as Omit<UpdateUserPayload, 'id'>);

    if (user.photo) {
      const photo = Array.isArray(user.photo) ? user.photo[0] : user.photo;
      if (photo instanceof File) {
        // `POST users/{id}/photo` отдаёт того же пользователя, но беднее: без ролей, а телефон
        // и почта в нём необязательны. Поэтому ответ `PUT` им не подменяем, а дописываем в него
        // ровно то, ради чего ходили, — ссылку на фото (IMGM-1440).
        const uploaded = await playersResource.uploadPhoto(photo, user.id, tournamentId);
        if (uploaded.photo) {
          result.photo = uploaded.photo;
        }
      } else throw new Error('Invalid photo type');
    }

    return mapOrganizerUserResponseDto([result])[0];
  }

  public async deleteUser(tournamentId: number, params: DeleteUserPayload): Promise<boolean> {
    const { id: userId } = params;
    const response = await this.child(`${tournamentId}/users/${userId}`).delete<{ result: string }>();
    return response.result === 'ok';
  }
}

const roleUsers = new RoleUsers();
export default roleUsers;
